import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProperty } from '../context/PropertyContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tour } from './Tour';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Inbox,
  Clock,
  Star,
  Users,
  Building2,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ListTodo,
  Compass,
  Copy,
  ExternalLink
} from 'lucide-react';

// Operator-visible nav items (shown to all signed-in users)
const navItems = [
  { path: '/desk/today', label: 'Today', icon: LayoutDashboard },
  { path: '/desk/daily-run', label: 'Daily Run', icon: ListTodo },
  { path: '/desk/inbox', label: 'Guest Inbox', icon: Inbox },
  { path: '/desk/followups', label: 'Follow-ups', icon: Clock },
  { path: '/desk/reviews', label: 'Reviews', icon: Star },
  { path: '/desk/guests', label: 'Guests', icon: Users },
  { path: '/desk/reports', label: 'Reports', icon: FileText },
  { path: '/desk/properties', label: 'Client Workspaces', icon: Building2 },
];

// Admin-only nav entries — appended at runtime in the sidebar only when
// `user.role === 'ADMIN'`. Backend admin routes are independently guarded
// with `require_admin`, so an OPERATOR who manually navigates to one of
// these paths is redirected by `AdminRoute` in App.js and any direct API
// call returns 403.
const adminNavItems = [
  { path: '/desk/settings', label: 'Settings', icon: Settings },
];

// Showcase is available to ALL signed-in roles (admin gets full controls,
// operator gets a read-only practice landing).
const showcaseNavItem = { path: '/desk/demo', label: 'Showcase', icon: Compass };

// Small status pill used in the top bar / sidebar logo area. Shows whether
// the current session is the read-only DEMO sandbox or the LIVE workspace.
const ModePill = ({ isDemo }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase font-medium border ${
      isDemo
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-emerald-50 text-emerald-800 border-emerald-200'
    }`}
    data-testid={isDemo ? 'mode-pill-demo' : 'mode-pill-live'}
    title={isDemo ? 'You are signed in to the read-only demo sandbox' : 'You are signed in to the live workspace'}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${isDemo ? 'bg-amber-500' : 'bg-emerald-500'}`} />
    {isDemo ? 'Demo' : 'Live'}
  </span>
);

export const DeskLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const isDemoUser = user?.role === 'DEMO';
  const { properties, selectedPropertyId, setSelectedPropertyId } = useProperty();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Read tour-seen from BOTH user object and localStorage to avoid the
  // stale-state race that caused the tour to re-open after completion.
  const tourAlreadySeen = () => {
    if (user?.hasSeenTour) return true;
    try {
      const stored = JSON.parse(localStorage.getItem('maris_user') || '{}');
      return stored?.hasSeenTour === true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (user && !tourAlreadySeen()) {
      const timer = setTimeout(() => setShowTour(true), 500);
      return () => clearTimeout(timer);
    }
    setShowTour(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleTourComplete = () => {
    setShowTour(false);
    // Persist seen flag immediately so re-renders don't reopen the tour
    try {
      const stored = JSON.parse(localStorage.getItem('maris_user') || '{}');
      stored.hasSeenTour = true;
      localStorage.setItem('maris_user', JSON.stringify(stored));
    } catch {}
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-[#F3F5F7]">
      {/* Tour Modal */}
      <Tour show={showTour} onComplete={handleTourComplete} user={user} />
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-stone-200 z-50 flex items-center justify-between px-4">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          data-testid="mobile-menu-button"
        >
          <Menu className="h-5 w-5 text-stone-600" />
        </button>
        <div className="flex items-center gap-2">
          <span className="font-heading text-xl text-[#3F617E]">Maris Desk</span>
          <ModePill isDemo={isDemoUser} />
        </div>
        <div className="w-10" />
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-stone-200 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-stone-100">
            <Link to="/desk/today" className="flex items-center gap-2">
              <span className="font-heading text-2xl text-[#3F617E]">Maris Desk</span>
              <ModePill isDemo={isDemoUser} />
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-stone-100 rounded"
              data-testid="close-sidebar-button"
            >
              <X className="h-5 w-5 text-stone-500" />
            </button>
          </div>

          {/* Property Switcher */}
          <div className="px-4 py-4 border-b border-stone-100">
            <label className="text-xs font-medium text-stone-500 mb-2 block">Property Filter</label>
            <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
              <SelectTrigger className="w-full" data-testid="property-switcher">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-stone-500" />
                    All Properties
                  </div>
                </SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#3F617E]" />
                      {p.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Guest Hub portal shortcut — only when a single property is selected */}
            {selectedPropertyId && selectedPropertyId !== 'all' && (() => {
              const sel = properties.find(p => p.id === selectedPropertyId);
              if (!sel?.slug) return null;
              const url = `${window.location.origin}/guesthub/${sel.slug}`;
              return (
                <div className="mt-3 rounded-lg border border-[#3F617E]/20 bg-[#3F617E]/5 p-2.5" data-testid="sidebar-guesthub-cta">
                  <p className="text-[10px] font-semibold tracking-wide uppercase text-[#3F617E]">Guest Hub portal</p>
                  <p className="text-[11px] text-stone-600 mt-0.5 truncate">/guesthub/{sel.slug}</p>
                  <div className="flex gap-1.5 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] flex-1 rounded-full px-2"
                      onClick={(e) => {
                        e.preventDefault();
                        navigator.clipboard?.writeText(url);
                        toast?.success?.('Guest Hub link copied');
                      }}
                      data-testid="sidebar-copy-guesthub"
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                    <a href={`/guesthub/${sel.slug}`} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button size="sm" className="h-7 text-[11px] w-full bg-[#3F617E] hover:bg-[#345470] rounded-full px-2" data-testid="sidebar-open-guesthub">
                        <ExternalLink className="h-3 w-3 mr-1" /> Open
                      </Button>
                    </a>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
            {[...navItems, ...(user?.role === 'ADMIN' ? adminNavItems : []), showcaseNavItem].map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium
                    transition-all duration-200
                    ${isActive 
                      ? 'bg-[#3F617E] text-white shadow-sm' 
                      : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="p-4 border-t border-stone-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-stone-100 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-9 w-9 bg-[#3F617E]">
                    <AvatarFallback className="bg-[#3F617E] text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
                    <p className="text-xs text-stone-500">{user?.role}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="text-stone-600">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowTour(true)}
                  className="text-stone-700 focus:text-stone-900"
                  data-testid="take-tour-again-button"
                >
                  <Compass className="h-4 w-4 mr-2" />
                  Take tour again
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen overflow-x-hidden">
        <div className="p-4 sm:p-6 lg:p-8 max-w-full">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DeskLayout;
